import { createSlice } from "@reduxjs/toolkit";
import {
  fetchRecordCounts,
  fetchBlogPosts,
  fetchOutlookEvents,
  fetchOperationalUpdates,
  fetchPolicyDistribution,
  fetchCustomerAcquisitionData,
  fetchRetentionData,
  fetchARTrendData,
  fetchRenewalCounts,
  fetchBinderCounts,
  fetchNoticeAndEdocCounts,
  fetchTaskModuleWiseCounts,
  fetchCustomerSegmentation,
  fetchTotalCustomerCount,
  fetchRetentionDataV2,
  fetchGrossWrittenPremiumData,
} from "./dashboardThunk";
import { IDashboardState, IMetric } from "./dashboard.types";

const initialMetricState: IMetric = {
  data: [],
  total: 0,
  loading: false,
  error: null,
  lastUpdated: null,
};

export const initialDashboardState: IDashboardState = {
  metrics: {},
  blogs: { data: [], loading: false, error: null, lastUpdated: null },
  outlookEvents: { data: [], loading: false, error: null, lastUpdated: null },
  operationalUpdates: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    currentPage: 1,
    hasMore: true,
  },
  policyDistribution: { data: [], loading: false, error: null, lastUpdated: null },
  customerAcquisition: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    selectedYear: null,
    byYear: {},
  },
  retentionRate: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    selectedYear: null,
    byYear: {}
  },
  arTrend: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    selectedYear: null,
    byYear: {},
  },
  renewalCount: {
    data: [],
    loading: false,
    error: null,
  },
  binderCount: {
    data: [],
    loading: false,
    error: null,
  },
  taskModuleWiseCount: {
    data: [],
    loading: false,
    error: null,
  },
  noticeAndEdocCount: {
    data: [],
    loading: false,
    error: null,
  },
  customerSegmentation: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
  },
  totalCustomerCount: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
  },
  grossWrittenPremium: {
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    selectedYear: null,
    byYear: {},
  },
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: initialDashboardState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ── Record Counts ────────────────────────────────────────────────────────
      .addCase(fetchRecordCounts.pending, (state, action) => {
        const { tableName, key } = action.meta.arg;
        const metricKey = key || tableName;
        if (!state.metrics[metricKey]) {
          state.metrics[metricKey] = { ...initialMetricState };
        }
        state.metrics[metricKey].loading = true;
        state.metrics[metricKey].error = null;
      })
      .addCase(fetchRecordCounts.fulfilled, (state, action) => {
        const { tableName, key, data } = action.payload;
        const metricKey = key || tableName;
        let metricsData = data || [];

        // Exclude "Voided" and "Completed" from change request counts
        if (metricKey === "latest_all_policy_change_request") {
          const excluded = ["voided", "completed"];
          metricsData = metricsData.filter(
            (item: any) => !excluded.includes((item.name || "").toLowerCase().trim())
          );
        }

        state.metrics[metricKey] = {
          data: metricsData,
          total: metricsData.reduce(
            (sum: number, item: any) => sum + (Number(item.cnt) || 0),
            0
          ),
          loading: false,
          error: null,
          lastUpdated: Date.now(),
        };
      })
      .addCase(fetchRecordCounts.rejected, (state, action) => {
        const { tableName, key } = action.meta.arg;
        const metricKey = key || tableName;
        if (!state.metrics[metricKey]) {
          state.metrics[metricKey] = { ...initialMetricState };
        }
        state.metrics[metricKey].loading = false;
        state.metrics[metricKey].error = (action.payload as string) || "Error fetching data";
      })

      // ── Blog Posts ───────────────────────────────────────────────────────────
      .addCase(fetchBlogPosts.pending, (state) => {
        state.blogs.loading = true;
        state.blogs.error = null;
      })
      .addCase(fetchBlogPosts.fulfilled, (state, action) => {
        state.blogs.data = action.payload || [];
        state.blogs.loading = false;
        state.blogs.error = null;
        state.blogs.lastUpdated = Date.now();
      })
      .addCase(fetchBlogPosts.rejected, (state, action) => {
        state.blogs.loading = false;
        state.blogs.error = (action.payload as string) || "Error fetching blogs";
      })

      // ── Outlook Events ───────────────────────────────────────────────────────
      .addCase(fetchOutlookEvents.pending, (state) => {
        state.outlookEvents.loading = true;
        state.outlookEvents.error = null;
      })
      .addCase(fetchOutlookEvents.fulfilled, (state, action) => {
        state.outlookEvents.data = action.payload || [];
        state.outlookEvents.loading = false;
        state.outlookEvents.error = null;
        state.outlookEvents.lastUpdated = Date.now();
      })
      .addCase(fetchOutlookEvents.rejected, (state, action) => {
        state.outlookEvents.loading = false;
        state.outlookEvents.error = (action.payload as string) || "Error fetching outlook events";
      })

      // ── Operational Updates ──────────────────────────────────────────────────
      .addCase(fetchOperationalUpdates.pending, (state) => {
        state.operationalUpdates.loading = true;
        state.operationalUpdates.error = null;
      })
      .addCase(fetchOperationalUpdates.fulfilled, (state, action: any) => {
        const { data, currentPage, hasMore } = action.payload;
        if (currentPage === 1) {
          state.operationalUpdates.data = data;
        } else {
          state.operationalUpdates.data = [...state.operationalUpdates.data, ...data];
        }
        state.operationalUpdates.currentPage = currentPage;
        state.operationalUpdates.hasMore = hasMore;
        state.operationalUpdates.loading = false;
        state.operationalUpdates.error = null;
        state.operationalUpdates.lastUpdated = Date.now();
      })
      .addCase(fetchOperationalUpdates.rejected, (state, action) => {
        state.operationalUpdates.loading = false;
        state.operationalUpdates.error = (action.payload as string) || "Error fetching operational updates";
      })

      // ── Policy Distribution ──────────────────────────────────────────────────
      .addCase(fetchPolicyDistribution.pending, (state) => {
        state.policyDistribution.loading = true;
        state.policyDistribution.error = null;
      })
      .addCase(fetchPolicyDistribution.fulfilled, (state, action) => {
        state.policyDistribution.data = action.payload || [];
        state.policyDistribution.loading = false;
        state.policyDistribution.error = null;
        state.policyDistribution.lastUpdated = Date.now();
      })
      .addCase(fetchPolicyDistribution.rejected, (state, action) => {
        state.policyDistribution.loading = false;
        state.policyDistribution.error = (action.payload as string) || "Error fetching policy distribution";
      })

      // ── Customer Acquisition ─────────────────────────────────────────────────
      .addCase(fetchCustomerAcquisitionData.pending, (state) => {
        state.customerAcquisition.loading = true;
        state.customerAcquisition.error = null;
      })
      .addCase(fetchCustomerAcquisitionData.fulfilled, (state, action: any) => {
        state.customerAcquisition.data = action.payload.data || [];
        state.customerAcquisition.selectedYear = action.payload.year;
        if (action.payload.year) {
          state.customerAcquisition.byYear[action.payload.year] = action.payload.data || [];
        }
        state.customerAcquisition.loading = false;
        state.customerAcquisition.error = null;
        state.customerAcquisition.lastUpdated = Date.now();
      })
      .addCase(fetchCustomerAcquisitionData.rejected, (state, action) => {
        state.customerAcquisition.loading = false;
        state.customerAcquisition.error = (action.payload as string) || "Error fetching customer acquisition data";
      })


      // ─── Gross Written Premium ─────────────────────────────────────────────────────────────────
      .addCase(fetchGrossWrittenPremiumData.pending, (state) => {
        state.grossWrittenPremium.loading = true;
        state.grossWrittenPremium.error = null;
      })
      .addCase(fetchGrossWrittenPremiumData.fulfilled, (state, action: any) => {
        state.grossWrittenPremium.data = action.payload.data || [];
        state.grossWrittenPremium.selectedYear = action.payload.year;
        if (action.payload.year) {
          state.grossWrittenPremium.byYear[action.payload.year] = action.payload.data || [];
        }
        state.grossWrittenPremium.loading = false;
        state.grossWrittenPremium.error = null;
        state.grossWrittenPremium.lastUpdated = Date.now();
      })
      .addCase(fetchGrossWrittenPremiumData.rejected, (state, action) => {
        state.grossWrittenPremium.loading = false;
        state.grossWrittenPremium.error = (action.payload as string) || "Error fetching customer acquisition data";
      })


      // ── Policy Retention Rate ────────────────────────────────────────────────
      .addCase(fetchRetentionData.pending, (state) => {
        state.retentionRate.loading = true;
        state.retentionRate.error = null;
      })
      .addCase(fetchRetentionData.fulfilled, (state, action: any) => {
        state.retentionRate.data = action.payload.data || [];
        state.retentionRate.selectedYear = action.payload.year;
        state.retentionRate.loading = false;
        state.retentionRate.error = null;
        state.retentionRate.lastUpdated = Date.now();
      })
      .addCase(fetchRetentionData.rejected, (state, action) => {
        state.retentionRate.loading = false;
        state.retentionRate.error = (action.payload as string) || "Error fetching retention data";
      })

      // ── Policy Retention V2 Rate ────────────────────────────────────────────────
      .addCase(fetchRetentionDataV2.pending, (state) => {
        state.retentionRate.loading = true;
        state.retentionRate.error = null;
      })
      .addCase(fetchRetentionDataV2.fulfilled, (state, action: any) => {
        state.retentionRate.data = action.payload.data || [];
        state.retentionRate.selectedYear = action.payload.year;
        if (action.payload.year) {
          state.retentionRate.byYear[action.payload.year] = action.payload.data || [];
        }
        state.retentionRate.loading = false;
        state.retentionRate.error = null;
        state.retentionRate.lastUpdated = Date.now();
      })
      .addCase(fetchRetentionDataV2.rejected, (state, action) => {
        state.retentionRate.loading = false;
        state.retentionRate.error = (action.payload as string) || "Error fetching customer acquisition data";
      })


      // ── AR Trend ─────────────────────────────────────────────────────────────────
      .addCase(fetchARTrendData.pending, (state) => {
        state.arTrend.loading = true;
        state.arTrend.error = null;
      })
      .addCase(fetchARTrendData.fulfilled, (state, action: any) => {
        state.arTrend.data = action.payload.data || [];
        state.arTrend.selectedYear = action.payload.year;
        if (action.payload.year) {
          state.arTrend.byYear[action.payload.year] = action.payload.data || [];
        }
        state.arTrend.loading = false;
        state.arTrend.error = null;
        state.arTrend.lastUpdated = Date.now();
      })
      .addCase(fetchARTrendData.rejected, (state, action) => {
        state.arTrend.loading = false;
        state.arTrend.error = (action.payload as string) || "Error fetching AR trend data";
      })


      // ── Renewal Count ─────────────────────────────────────────────────────────────────
      .addCase(fetchRenewalCounts.pending, (state) => {
        state.renewalCount.loading = true;
        state.renewalCount.error = null;
      })
      .addCase(fetchRenewalCounts.fulfilled, (state, action: any) => {
        state.renewalCount.data = action.payload
        state.renewalCount.loading = false;
        state.renewalCount.error = null;
      })
      .addCase(fetchRenewalCounts.rejected, (state, action) => {
        state.renewalCount.loading = false;
        state.renewalCount.error = (action.payload as string) || "Error fetching renewal count data";
      })

      // ── Binder Count ─────────────────────────────────────────────────────────────────
      .addCase(fetchBinderCounts.pending, (state) => {
        state.binderCount.loading = true;
        state.binderCount.error = null;
      })
      .addCase(fetchBinderCounts.fulfilled, (state, action: any) => {
        state.binderCount.data = action.payload
        state.binderCount.loading = false;
        state.binderCount.error = null;
      })
      .addCase(fetchBinderCounts.rejected, (state, action) => {
        state.binderCount.loading = false;
        state.binderCount.error = (action.payload as string) || "Error fetching binder count data";
      })

      // ── Notice and Edoc Count ─────────────────────────────────────────────────────────────────
      .addCase(fetchNoticeAndEdocCounts.pending, (state) => {
        state.noticeAndEdocCount.loading = true;
        state.noticeAndEdocCount.error = null;
      })
      .addCase(fetchNoticeAndEdocCounts.fulfilled, (state, action: any) => {
        state.noticeAndEdocCount.data = action.payload
        state.noticeAndEdocCount.loading = false;
        state.noticeAndEdocCount.error = null;
      })
      .addCase(fetchNoticeAndEdocCounts.rejected, (state, action) => {
        state.noticeAndEdocCount.loading = false;
        state.noticeAndEdocCount.error = (action.payload as string) || "Error fetching notice and edoc count data";
      })

      // ── Task Module Wise Count ─────────────────────────────────────────────────────────────────
      .addCase(fetchTaskModuleWiseCounts.pending, (state) => {
        state.taskModuleWiseCount.loading = true;
        state.taskModuleWiseCount.error = null;
      })
      .addCase(fetchTaskModuleWiseCounts.fulfilled, (state, action: any) => {
        state.taskModuleWiseCount.data = action.payload;
        state.taskModuleWiseCount.loading = false;
        state.taskModuleWiseCount.error = null;
      })
      .addCase(fetchTaskModuleWiseCounts.rejected, (state, action) => {
        state.taskModuleWiseCount.loading = false;
        state.taskModuleWiseCount.error = (action.payload as string) || "Error fetching task module wise count data";
      })
      // ── Customer Segmentation ─────────────────────────────────────────────────────────────────
      .addCase(fetchCustomerSegmentation.pending, (state) => {
        state.customerSegmentation.loading = true;
        state.customerSegmentation.error = null;
      })
      .addCase(fetchCustomerSegmentation.fulfilled, (state, action: any) => {
        state.customerSegmentation.data = action.payload || [];
        state.customerSegmentation.loading = false;
        state.customerSegmentation.error = null;
        state.customerSegmentation.lastUpdated = Date.now();
      })
      .addCase(fetchCustomerSegmentation.rejected, (state, action) => {
        state.customerSegmentation.loading = false;
        state.customerSegmentation.error =
          (action.payload as string) ||
          "Error fetching customer segmentation data";
      })
      // ── Total Customer Count ─────────────────────────────────────────────────────────────────
      .addCase(fetchTotalCustomerCount.pending, (state) => {
        state.totalCustomerCount.loading = true;
        state.totalCustomerCount.error = null;
      })
      .addCase(fetchTotalCustomerCount.fulfilled, (state, action: any) => {
        state.totalCustomerCount.data = action.payload || [];
        state.totalCustomerCount.loading = false;
        state.totalCustomerCount.error = null;
        state.totalCustomerCount.lastUpdated = Date.now();
      })
      .addCase(fetchTotalCustomerCount.rejected, (state, action) => {
        state.totalCustomerCount.loading = false;
        state.totalCustomerCount.error =
          (action.payload as string) ||
          "Error fetching total customer count data";
      });
  },
});

export default dashboardSlice.reducer;